// Anonymous click counts for "popular" lists. Three counters per story:
// `opens` (its Pulse story page was opened), `sources` (a link out to the
// official Databricks page was clicked) and `saves` (added to a Reading Basket;
// removing it doesn't subtract). Stored twice: an all-time total
// (clicks/{storyId}) and a per-day bucket (clickDays/{day}_{storyId}, day =
// UTC days since the epoch) for "popular this week/month". No user ID, cookie,
// or IP is stored; the security rules only allow adding 1 at a time.

export type ClickKind = "opens" | "sources" | "saves";
export const CLICK_KINDS: readonly ClickKind[] = ["opens", "sources", "saves"];

const DAY_MS = 86_400_000;

/** UTC day number (days since 1970-01-01); matches the rules' math.floor(request.time.toMillis() / 86400000). */
export function utcDay(ms: number): number {
  return Math.floor(ms / DAY_MS);
}

export function clickDayId(day: number, storyId: string): string {
  return `${day}_${storyId}`;
}

/** countryDays/{day}_{CC}: the same counters per approximate reader country ("ZZ" = unknown). */
export function countryDayId(day: number, country: string): string {
  return `${day}_${country}`;
}

/** "2026-11-03" for a UTC day number. */
export function utcDayIso(day: number): string {
  return new Date(day * DAY_MS).toISOString().slice(0, 10);
}

export interface ClickTotals {
  storyId: string;
  opens: number;
  sources: number;
  /** Missing on counts recorded before saves were tracked. */
  saves?: number;
}

/** Sums day buckets per story and ranks by the chosen counter (ties: the other counters, then ID). */
export function rankClicks(rows: ClickTotals[], by: ClickKind, top = 10): ClickTotals[] {
  const sum = new Map<string, ClickTotals>();
  for (const r of rows) {
    const t = sum.get(r.storyId) ?? { storyId: r.storyId, opens: 0, sources: 0, saves: 0 };
    t.opens += r.opens ?? 0;
    t.sources += r.sources ?? 0;
    t.saves = (t.saves ?? 0) + (r.saves ?? 0);
    sum.set(r.storyId, t);
  }
  const n = (t: ClickTotals, k: ClickKind) => t[k] ?? 0;
  const others = CLICK_KINDS.filter((k) => k !== by);
  return [...sum.values()]
    .filter((t) => n(t, by) > 0)
    .sort((a, b) => n(b, by) - n(a, by) || others.reduce((d, k) => d || n(b, k) - n(a, k), 0) || a.storyId.localeCompare(b.storyId))
    .slice(0, top);
}
